import React from 'react'
import {
  Box,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { MoreVertical } from 'react-feather'
import { toast } from 'react-toastify'
import Pill from '../../components/Pill'
import SearchBar from '../../components/SearchBar'
import colors from '../../colors'
import Fuse from 'fuse.js'
import { useNavigate } from 'react-router-dom'
import useMembershipsStore from '../../stores/memberships'
import { Membership } from '../../types'
import useAuthStore from '../../stores/auth'
import axios from '../../utils/axios'

const formatCents = (cents: number) => {
  const dollars = cents / 100
  // Whole dollars are the norm here; only show cents when there are any.
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

const PriceCell: React.FC<{ membership: Membership }> = ({ membership }) => {
  const parts: string[] = []
  if (membership.monthlyAmount != null) parts.push(`${formatCents(membership.monthlyAmount)}/mo`)
  if (membership.annualAmount != null) parts.push(`${formatCents(membership.annualAmount)}/yr`)

  if (parts.length === 0) return <span>—</span>

  return <span>{parts.join(' · ')}</span>
}

const MembershipTypeChip: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return <>-</>

  const typeConfig: Record<string, { label: string; color: 'primary' | 'default' }> = {
    'employee_benefit': { label: 'Employee Benefit', color: 'primary' },
    'standard': { label: 'Standard', color: 'default' },
  }

  const config = typeConfig[type] || { label: type, color: 'default' as const }

  return <Chip label={config.label} color={config.color} size="small" />
}

export default function Page() {
  const { memberships, getMemberships, updateMembership } = useMembershipsStore()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [searchVal, setSearchVal] = React.useState('')
  const [loading, setLoading] = React.useState<boolean>(true)
  const [searchedMemberships, setSearchedMemberships] = React.useState(Object.values(memberships))
  // Anchored to coordinates, not the button element: MembershipTable is
  // declared inside this component, so any state change remounts the table and
  // detaches an element anchor, which MUI then positions at the top-left.
  const [menuPosition, setMenuPosition] = React.useState<{ top: number, left: number } | null>(null)
  const [menuMembership, setMenuMembership] = React.useState<Membership | null>(null)
  const [updatingStatus, setUpdatingStatus] = React.useState(false)
  // Org admins manage their own memberships; employees are read-only.
  const canManageMemberships = user?.role === 'admin' || user?.role === 'super_admin'
  // Read live rather than from the persisted auth store: that is populated at
  // login, so connecting Stripe mid-session would otherwise still look
  // unconnected until the user logged out and back in.
  const [stripeConnected, setStripeConnected] = React.useState<boolean>(
    user?.organization?.stripeConnected === true
  )

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/settings')
        setStripeConnected(res.data.stripe === true)
      } catch {
        // Leave the cached value in place; the server still enforces the gate.
      }
    })()
  }, [])

  // Only the transitions the product actually supports. There is no way to
  // close or archive a membership from here, because that behaviour does not
  // exist yet.
  const statusActions = (status?: string): { label: string, next: string, disabled?: boolean }[] => {
    switch (status) {
      case 'draft':
        // Publishing without Stripe would let patrons pay with nowhere for the
        // money to settle, so the server rejects it - say why up front.
        return stripeConnected
          ? [{ label: 'Make live', next: 'live' }]
          : [{ label: 'Connect Stripe to publish', next: 'live', disabled: true }]
      case 'live':
        return [{ label: 'Hide from new signups', next: 'hidden' }]
      case 'hidden':
        return [{ label: 'Make live', next: 'live' }]
      default:
        return []
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, membership: Membership) => {
    event.stopPropagation() // the row itself navigates
    const rect = event.currentTarget.getBoundingClientRect()
    setMenuPosition({ top: rect.bottom, left: rect.right })
    setMenuMembership(membership)
  }

  const closeMenu = () => {
    setMenuPosition(null)
    setMenuMembership(null)
  }

  const handleStatusChange = async (next: string) => {
    if (!menuMembership) return

    setUpdatingStatus(true)
    const result = await updateMembership(menuMembership.uuid, { status: next })
    setUpdatingStatus(false)
    closeMenu()

    if (typeof result === 'string') {
      toast.error(result)
      return
    }

    toast.success(next === 'live' ? 'Membership is now live' : 'Membership hidden from new signups')
    await getMemberships()
  }
  const showWalletColumn = user?.organization?.appleWalletEnabled === true
  const fuse = React.useMemo(() => new Fuse<Membership>(Object.values(memberships), {
    keys: ['title'],
    distance: 0,
    threshold: 0,
    useExtendedSearch: true,
  }), [memberships])

  React.useEffect(() => {
    (async () => {
      await getMemberships()
      setLoading(false)
    })()
  }, [getMemberships])


  React.useEffect(() => {
    if (!searchVal) {
      setSearchedMemberships(Object.values(memberships))
      return
    }

    const searchResults = fuse.search(searchVal).map((r) => r.item)

    setSearchedMemberships(searchResults)
  }, [searchVal, memberships, fuse])

  const handleSearchChange = (newValue: string) => {
    setSearchVal(newValue)
  }

  const styles = useStyles()

  const handleRowClicked = (membershipId: string) => {
    navigate(`/memberships/${membershipId}`)
  }

  const handleAddMembershipClick = () => {
    navigate('/memberships/new');
  }

  const MembershipTable = () => {
    if (loading) {
      return (
        <Box className={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      )
    }
    return (
      <>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Membership</TableCell>
                <TableCell>Price</TableCell>
                <TableCell align="right">Benefits</TableCell>
                <TableCell>Active Patrons</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                {showWalletColumn && <TableCell>Wallet</TableCell>}
                {canManageMemberships && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableContainer}>
              {searchedMemberships.map((membership) => {
                return (
                  <TableRow hover className={styles.row} key={membership.id} onClick={() => handleRowClicked(membership.uuid)}>
                    <TableCell>
                      <strong>{membership.title}</strong>
                      {membership.recommended && (
                        <span className={styles.recommendedBadge}>RECOMMENDED</span>
                      )}
                    </TableCell>
                    <TableCell><PriceCell membership={membership} /></TableCell>
                    <TableCell align="right">{membership.benefitCount ?? 0}</TableCell>
                    <TableCell>{membership.activeSubscriptionCount}</TableCell>
                    <TableCell><MembershipTypeChip type={membership.membershipType} /></TableCell>
                    <TableCell>{membership.status ? <Pill value={membership.status} color={['live', 'hidden'].includes(membership.status) ? 'green' : 'red'} /> : `-`}</TableCell>
                    {showWalletColumn && (
                      <TableCell>
                        <Chip
                          label={membership.walletEnabled ? 'Enabled' : 'Disabled'}
                          color={membership.walletEnabled ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    )}
                    {canManageMemberships && (
                      <TableCell align="right">
                        {statusActions(membership.status).length > 0 && (
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${membership.title}`}
                            onClick={(e) => openMenu(e, membership)}
                          >
                            <MoreVertical size={18} />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    )
  }

  if (!searchedMemberships) {
    return (
      <Box mt={25} className={styles.buttonContainer}>
        <Typography mb={3} variant="h6">You don't have any memberships yet</Typography>
        {canManageMemberships && (
          <Button className={styles.addButton} onClick={handleAddMembershipClick}>Add Membership +</Button>
        )}
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Box mb={2} className={styles.controlsContainer}>
        <Box mr={2} className={styles.searchContainer}>
          <SearchBar fullWidth value={searchVal} onChange={handleSearchChange} />
        </Box>
        {canManageMemberships && (
          <Button variant="contained" color="primary" onClick={handleAddMembershipClick}>
            Add Membership
          </Button>
        )}
      </Box>
      <MembershipTable />

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ?? undefined}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(menuPosition)}
        onClose={closeMenu}
      >
        {statusActions(menuMembership?.status).map((action) => (
          <MenuItem
            key={action.next}
            disabled={updatingStatus || action.disabled}
            onClick={() => handleStatusChange(action.next)}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

const useStyles = makeStyles({
  recommendedBadge: {
    marginLeft: 8,
    padding: '2px 6px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    backgroundColor: colors.PALE_BLUE,
    color: colors.BRIGHT_BLUE,
    verticalAlign: 'middle',
  },
  exportContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  checksContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 250,
    flexWrap: 'wrap',
  },
  container: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tableContainer: {
    height: '100%',
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  leftMenuContainer: {
    width: 300,
    height: '100%',
    borderRight: `2px solid ${colors.GREY_6}`
  },
  closeButton: {
    width: 45,
    height: 45,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: "100%",
    background: colors.GREY_2,
    cursor: 'pointer',
  },
  addButton: {
    width: 200,
  },
  closeContainer: {
    position: 'relative',
    background: colors.STITCH_BLUE,
    color: colors.WHITE,
    display: 'flex',
    borderTopLeftRadius: 20,
  },
  controlsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  searchContainer: {
    width: '40%',
    flex: '0 1 auto',
  },
  filtersContainer: {
    display: 'flex',
  },
  boldText: {
    fontWeight: 'bold',
  },
  row: {
    cursor: 'pointer',
  },
  mrr: {
    color: colors.DARK_GREEN,
    fontWeight: 'bold',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 200
  }
})
